import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../services/api';
import { notify } from 'messages';
import { flattenQuery } from 'helpers/flattenQuery';
import type { FolderPath } from 'helpers/folderPaths';
import { FilterConfig } from 'hooks/useListQuery';

export interface Queue {
  queue_id: number;
  queue_limit: number;
  queue_name: string;
  queue_description?: string | null;
  queue_status: 'Open' | 'Closed' | string;
  zone_name: string;
  workers?: any;
  connections?: any;
  scheduling_window_id?: number | null;
  queue_allocation_statuses?: string[];
  // Folder support (nap-backend #1076). Zone-scoped.
  folder_id?: number | null;
  folder_path?: FolderPath | null;
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface QueuesState {
  queues: Queue[];
  selectedQueue: Queue | null;
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    getQueuesPaginated: boolean;
    getQueueById: boolean;
  };
}

interface RejectValue {
  message: string;
  error: string;
}

const initialState: QueuesState = {
  queues: [],
  selectedQueue: null,
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    getQueuesPaginated: false,
    getQueueById: false,
  },
};

export const getQueuesPaginated = createAsyncThunk('queues/getQueuesPaginated', async (params: Record<string, any>) => {
  const queryParams = {
    limit: initialState.pagination.pageSize,
    ...params,
  };
  const res = await api.get('/queues', { params: flattenQuery(queryParams) });
  return res.data;
});

export const getQueues = createAsyncThunk('queues/getQueues', async () => {
  const response = await api.get('/queues');
  return response.data;
});

export const getQueueById = createAsyncThunk('queues/getQueueById', async (queueId: number) => {
  const response = await api.get('/queues', {
    params: { queue_id: queueId },
  });
  if (response.data.queues && response.data.queues.length > 0) {
    return response.data.queues[0];
  }
  return null;
});

interface UpdateQueuePayload {
  queue_id: number;
  queue_limit?: number;
  queue_name: string;
  queue_new_name?: string;
  queue_description?: string | null;
  zone_name: string;
  zone_new_name?: string;
  queue_status: string;
  workers: any;
  connections: any;
  scheduling_window_id?: number | null;
  queue_allocation_statuses?: string[];
  folder_id: number | null;
}

interface CreateQueuePayload {
  queue_name: string;
  queue_status: string;
  zone_name: string;
  queue_description?: string | null;
  queue_limit: number;
  workers: any;
  connections: any;
  scheduling_window_id?: number | null;
  queue_allocation_statuses?: string[];
  folder_id: number | null;
}

export const updateQueue = createAsyncThunk<any, UpdateQueuePayload, { rejectValue: RejectValue }>(
  'queues/updateQueue',
  async (
    {
      queue_id,
      queue_name,
      queue_new_name,
      queue_limit,
      zone_new_name,
      zone_name,
      queue_status,
      queue_description,
      workers,
      connections,
      scheduling_window_id,
      queue_allocation_statuses,
      folder_id,
    }: UpdateQueuePayload,
    { rejectWithValue }
  ) => {
    const requestData = {
      queue_id,
      queue_name: queue_new_name,
      queue_limit,
      queue_status,
      zone_name: zone_new_name,
      queue_description,
      workers,
      connections,
      scheduling_window_id,
      queue_allocation_statuses,
      folder_id,
    };

    try {
      const filteredRequestData = Object.fromEntries(Object.entries(requestData).filter(([_, v]) => v !== undefined));

      const response = await api.put(`/queues/${encodeURIComponent(zone_name)}/${encodeURIComponent(queue_name)}`, filteredRequestData);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { error: err.message });
    }
  }
);

export const openQueue = createAsyncThunk(
  'queues/openQueue',
  async ({ queue_id, zone_name, queue_name }: { queue_id: number; zone_name: string; queue_name: string }) => {
    await api.put(`/queues/${encodeURIComponent(zone_name)}/${encodeURIComponent(queue_name)}/open`);
    return queue_id;
  }
);

export const closeQueue = createAsyncThunk(
  'queues/closeQueue',
  async ({ queue_id, zone_name, queue_name }: { queue_id: number; zone_name: string; queue_name: string }) => {
    await api.put(`/queues/${encodeURIComponent(zone_name)}/${encodeURIComponent(queue_name)}/close`);
    return queue_id;
  }
);

export const deleteQueue = createAsyncThunk<number, { queue_id: number; zone_name: string; queue_name: string }, { rejectValue: RejectValue }>(
  'queues/deleteQueue',
  async ({ queue_id, zone_name, queue_name }, { rejectWithValue }) => {
    try {
      await api.delete(`/queues/${encodeURIComponent(zone_name)}/${encodeURIComponent(queue_name)}`);
      return queue_id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { error: err.message });
    }
  }
);

export const createQueue = createAsyncThunk<Queue, CreateQueuePayload, { rejectValue: RejectValue }>(
  'queues/createQueue',
  async (
    {
      queue_name,
      queue_status,
      zone_name,
      queue_description,
      queue_limit,
      workers,
      connections,
      scheduling_window_id,
      queue_allocation_statuses,
      folder_id,
    }: CreateQueuePayload,
    { rejectWithValue }
  ) => {
    try {
      const body = {
        queue_name,
        queue_status,
        zone_name,
        queue_description,
        queue_limit,
        workers,
        connections,
        folder_id,
        ...(scheduling_window_id !== undefined ? { scheduling_window_id } : {}),
        ...(queue_allocation_statuses !== undefined ? { queue_allocation_statuses } : {}),
      };

      const response = await api.post('/queues', body);
      return response.data;
    } catch (err: any) {
      return rejectWithValue({
        message: err.message || 'Failed to create queue',
        error: err.response?.data?.error || 'Unknown error',
      });
    }
  }
);

const queuesSlice = createSlice({
  name: 'queues',
  initialState,
  reducers: {
    clearSelectedQueue: (state) => {
      state.selectedQueue = null;
    },
  },
  extraReducers: (builder) => {
    // non-paginated
    builder
      .addCase(getQueues.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getQueues.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.queues = action.payload.queues;
      })
      .addCase(getQueues.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch queues';
      });

    builder
      .addCase(getQueuesPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getQueuesPaginated = true;
      })
      .addCase(getQueuesPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { queues, config, filtersConfig, pagination } = action.payload;

        state.queues = queues;
        state.config = config;
        state.filtersConfig = filtersConfig || [];

        state.pagination.totalItems = pagination?.total_records ?? 0;
        state.pagination.totalPages = pagination?.total_pages ?? 0;
        state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;
        state.pagination.currentPage = (pagination?.current_page ?? 1) > (pagination?.total_pages ?? 0) ? 1 : (pagination?.current_page ?? 1);

        state.loading.getQueuesPaginated = false;
      })
      .addCase(getQueuesPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch queues';
        state.loading.getQueuesPaginated = false;
      });
    builder
      .addCase(getQueueById.pending, (state) => {
        state.loading.getQueueById = true;
      })
      .addCase(getQueueById.fulfilled, (state, action) => {
        state.selectedQueue = action.payload;
        state.loading.getQueueById = false;
      })
      .addCase(getQueueById.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch queue';
        state.loading.getQueueById = false;
      });

    builder
      .addCase(updateQueue.fulfilled, (state, action) => {
        const updated = action.payload as Queue;
        const index = state.queues.findIndex((queue) => queue.queue_id === updated.queue_id);
        if (index !== -1) state.queues[index] = updated;

        const messageKey = 'data-updated-queue';
        if (!notify.isShowing(messageKey)) {
          notify.success(`Queue updated successfully!`, { key: messageKey });
        }
      })
      .addCase(updateQueue.rejected, (state, action) => {
        state.error = (action.payload as any)?.error || 'Failed to update queue';
        const messageKey = 'data-failed-to-update-queue';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to update queue! ${state.error}`, { key: messageKey });
        }
      })
      // Handle deleteQueue
      .addCase(deleteQueue.fulfilled, (state, action) => {
        state.queues = state.queues.filter((queue) => queue.queue_id !== action.payload);
        const messageKey = 'data-deleted-queue';
        if (!notify.isShowing(messageKey)) {
          notify.success(`Queue deleted successfully!`, { key: messageKey });
        }
      })
      .addCase(deleteQueue.rejected, (state, action) => {
        state.error = (action.payload as any)?.error || 'Failed to delete queue';
        const messageKey = 'data-failed-to-delete-queue';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to delete queue! ${state.error}`, { key: messageKey });
        }
      })
      // Handle createQueue
      .addCase(createQueue.fulfilled, (state, action) => {
        state.queues.push(action.payload as Queue);
        const messageKey = 'data-create-queue';
        if (!notify.isShowing(messageKey)) {
          notify.success(`Queue created successfully!!`, { key: messageKey });
        }
      })
      .addCase(createQueue.rejected, (state, action) => {
        state.error = (action.payload as any)?.error || 'Failed to create queue';
        const messageKey = 'data-failed-to-create-queue';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to create queue! ${state.error}`, { key: messageKey });
        }
      })
      // Handle openQueue
      .addCase(openQueue.fulfilled, (state, action) => {
        const index = state.queues.findIndex((queue) => queue.queue_id === action.payload);
        if (index !== -1) {
          state.queues[index].queue_status = 'open';
        }
        const messageKey = 'data-open-queue';
        if (!notify.isShowing(messageKey)) {
          notify.success(`Successfully changed queue status to 'Open'!`, {
            key: messageKey,
          });
        }
      })
      .addCase(openQueue.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to open queue';
        const messageKey = 'data-failed-to-open-queue';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to change queue status to 'Open'`, { key: messageKey });
        }
      })
      // Handle closeQueue
      .addCase(closeQueue.fulfilled, (state, action) => {
        const index = state.queues.findIndex((queue) => queue.queue_id === action.payload);
        if (index !== -1) {
          state.queues[index].queue_status = 'closed';
        }
        const messageKey = 'data-close-queue';
        if (!notify.isShowing(messageKey)) {
          notify.success(`Successfully changed queue status to 'Closed'!`, {
            key: messageKey,
          });
        }
      })
      .addCase(closeQueue.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to close queue';
        const messageKey = 'data-failed-to-close-queue';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to change queue status to 'Closed'!`, {
            key: messageKey,
          });
        }
      });
  },
});

export const { clearSelectedQueue } = queuesSlice.actions;
export default queuesSlice.reducer;
